import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { getRequest } from "../helpers/api.helper";
import { toast } from "react-toastify";
import { UPLOAD_URL } from "../utils/constants";
import { shouldBlurProfileImage } from "../utils/imagePrivacy";
import Navbar from "./MyWebSite/Components/Navbar";

const QuickSearchResults = () => {
  const { id, name } = useParams(); // Get 'id' and 'pageName' from URL
  const location = useLocation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNext, setLoadingNext] = useState(false);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [profilesPerPage] = useState(10);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [autoLoadTimer, setAutoLoadTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const loadMoreRef = useRef(null);
  const SCROLL_KEY_Y = "quickSearch:lastScrollY";
  const SCROLL_KEY_PATH = "quickSearch:lastPath";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryData = Object.fromEntries(params.entries());

    if (Object.keys(queryData).length > 0) {
      setSearchCriteria(queryData);
      performAdvancedSearch(queryData, 1, false);
    } else if (location.state?.searchData) {
      setSearchCriteria(location.state.searchData);
      performAdvancedSearch(location.state.searchData, 1, false);
    } else {
      toast.error("No search criteria provided");
      setLoading(false);
    }
  }, [location.search, location.state]);

  const performAdvancedSearch = useCallback(
    async (searchData: any, pageNum = 1, append = false) => {
      if (!searchData) return;

      if (!append) setLoading(true);
      else setLoadingNext(true);

      setError(null);

      try {
        const params = new URLSearchParams();

        // add all search criteria
        Object.entries(searchData).forEach(([k, v]) => {
          if (v === undefined || v === null || v === "" || v === "Any") return;
          params.set(k, String(v));
        });

        // ✅ IMPORTANT: add pagination
        params.set("page", String(pageNum));
        params.set("limit", String(profilesPerPage));

        const response = await getRequest(
          `/bureau/website/${id}/search?${params.toString()}`
        );
        if (!response.success) {
          toast.error(response.message || "Failed to fetch results.");
          setHasMore(false);
          if (!append) setProfiles([]);
          return;
        }

        const data = response.data ?? {};
        const users = data.users || [];
        const total = data.total ?? 0;

        if (Array.isArray(users) && users.length > 0) {
          setProfiles((prev: any[]) => (append ? [...prev, ...users] : users));
          setTotalProfiles(total);
          setCurrentPage(pageNum);

          const totalPages = Math.ceil(total / profilesPerPage);
          setHasMore(pageNum < totalPages);

          startAutoLoadTimer();
        } else {
          setHasMore(false);
          if (!append) setProfiles([]);
        }
      } catch (err) {
        console.error(err);
        toast.error("Error performing search. Please try again.");
        if (!append) setProfiles([]);
      } finally {
        setLoading(false);
        setLoadingNext(false);
      }
    },
    [id, profilesPerPage]
  );

  // Start auto-load timer
  const startAutoLoadTimer = useCallback(() => {
    if (autoLoadTimer) clearTimeout(autoLoadTimer);
    const timer = setTimeout(() => {
      if (hasMore) {
        handleLoadNext();
      }
    }, 15000);
    setAutoLoadTimer(timer);
  }, [hasMore]);

  // Manual load next batch
  const handleLoadNext = useCallback(() => {
    if (autoLoadTimer) clearTimeout(autoLoadTimer);
    if (hasMore && !loadingNext && searchCriteria) {
      performAdvancedSearch(searchCriteria, currentPage + 1, true);
    }
  }, [
    hasMore,
    loadingNext,
    currentPage,
    searchCriteria,
    performAdvancedSearch,
    autoLoadTimer,
  ]);

  // Initial load
  useEffect(() => {
    return () => {
      if (autoLoadTimer) clearTimeout(autoLoadTimer);
    };
  }, [autoLoadTimer]);

  // Intersection Observer for scroll-based loading
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingNext && !loading) {
          handleLoadNext();
        }
      },
      { threshold: 0.1 }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [hasMore, loadingNext, loading]);

  const handleBackClick = () => {
    navigate("/search");
  };

  useEffect(() => {
    if (loading || profiles.length === 0) return;

    try {
      const lastPath = sessionStorage.getItem(SCROLL_KEY_PATH);
      const currentPath = window.location.pathname + window.location.search;

      if (!lastPath || lastPath !== currentPath) return;

      const y = Number(sessionStorage.getItem(SCROLL_KEY_Y) || 0);

      const restore = () => {
        window.scrollTo({ top: y, behavior: "auto" });

        // retry once more if content height not ready
        setTimeout(() => {
          if (Math.abs(window.scrollY - y) > 150) {
            window.scrollTo({ top: y, behavior: "auto" });
          }
        }, 300);
      };

      requestAnimationFrame(restore);
    } catch {}
  }, [loading, profiles.length, location.key]);

  const calculateAge = (dob: any) => {
    const birthDate = new Date(dob);
    const currentDate = new Date();
    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    if (
      month < birthDate.getMonth() ||
      (month === birthDate.getMonth() && day < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const [bureauName, setBureauName] = useState<string>();
  const [navbarLogo, setNavbarLogo] = useState<string>();

  useEffect(() => {
    const fetchBureauDetails = async () => {
      try {
        const response = await getRequest(`/bureau/website/${id}`);
        if (response.success) {
          const { profile } = response.data;
          setBureauName(profile.bureauName);
          setNavbarLogo(profile.navbarLogo);
        }
      } catch (error) {
        console.error("Error fetching bureau details:", error);
      }
    };

    fetchBureauDetails();
  }, []);

  if (loading)
    return (
      <div className="flex flex-col bg-white min-h-screen">
        <Navbar bureauName={bureauName} navbarLogo={navbarLogo} />
        <div className="flex-1 p-4 mt-20 mb-20">
          <div className="flex justify-center items-center py-8">
            <Loader />
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col bg-white min-h-screen">
        <Navbar bureauName={bureauName} navbarLogo={navbarLogo} />
        <div className="flex-1 p-4 mt-20 mb-20">
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => navigate("/admin/search")}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Different Search
            </button>
          </div>
        </div>
      </div>
    );

  const handleShareProfile = async (profile: any) => {
    try {
      const targetId = profile.martialId;
      if (!targetId) return toast.warn("Profile ID missing");

      const shareUrl = `https://matrimonystudio.in/${id}/${name}/${targetId}`;
      const shareText = `💍 Check out this profile on MeeMarriage: `;

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      // Mobile native share
      if (isMobile && navigator.share) {
        await navigator.share({
          title: "MeeMarriage Profile",
          text: shareText,
          url: shareUrl,
        });
        return;
      }

      // Desktop fallback → copy
      await navigator.clipboard.writeText(shareUrl);
      toast.info("Profile link copied. You can share it anywhere.");
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <div className="flex flex-col bg-white min-h-screen">
      <Navbar bureauName={bureauName} navbarLogo={navbarLogo} />

      <div className="flex-1 p-4 mb-20 mt-36 max-w-7xl mx-auto w-full">
        {/* Search Criteria Summary */}
        {searchCriteria && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-blue-800 mb-2">
                Search Criteria
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
              {searchCriteria.gender && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  <strong>Gender:</strong>{" "}
                  {searchCriteria.gender === "male" ? "Male" : "Female"}
                </span>
              )}
              {searchCriteria.ageMin && searchCriteria.ageMax && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  <strong>Age:</strong> {searchCriteria.ageMin} -{" "}
                  {searchCriteria.ageMax} years
                </span>
              )}
              {searchCriteria.heightMin && searchCriteria.heightMax && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  <strong>Height:</strong> {searchCriteria.heightMin} -{" "}
                  {searchCriteria.heightMax} ft
                </span>
              )}
              {searchCriteria.religionPreferences &&
                searchCriteria.religionPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Religion:</strong>{" "}
                    {searchCriteria.religionPreferences}
                  </span>
                )}
              {searchCriteria.castePreferences &&
                searchCriteria.castePreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Caste:</strong> {searchCriteria.castePreferences}
                  </span>
                )}
              {searchCriteria.educationPreferences &&
                searchCriteria.educationPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Education:</strong>{" "}
                    {searchCriteria.educationPreferences}
                  </span>
                )}
              {searchCriteria.occupationPreferences &&
                searchCriteria.occupationPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Occupation:</strong>{" "}
                    {searchCriteria.occupationPreferences}
                  </span>
                )}
              {searchCriteria.countryPreferences &&
                searchCriteria.countryPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>Country:</strong>{" "}
                    {searchCriteria.countryPreferences}
                  </span>
                )}
              {searchCriteria.statePreferences &&
                searchCriteria.statePreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>State:</strong> {searchCriteria.statePreferences}
                  </span>
                )}
              {searchCriteria.cityPreferences &&
                searchCriteria.cityPreferences !== "Any" && (
                  <span className="bg-blue-100 px-2 py-1 rounded">
                    <strong>City:</strong> {searchCriteria.cityPreferences}
                  </span>
                )}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="mb-4">
          <p className="text-gray-600">
            Found {totalProfiles} profile{totalProfiles !== 1 ? "s" : ""}{" "}
            matching your criteria
          </p>
          <button
            onClick={async () => {
              const url = window.location.href;

              try {
                await navigator.clipboard.writeText(url);
                if (navigator.share) {
                  await navigator.share({
                    title: "Mee Marriage Profiles",
                    text: "Check out these profiles on Mee Marriage",
                    url: url,
                  });
                } else {
                  toast.success("Search link copied!");
                }
              } catch (err) {
                console.error("Share failed:", err);
              }
            }}
            className="px-4 py-1 my-2 w-full max-w-96 bg-green-600 text-white rounded"
          >
            Share All Profiles
          </button>
        </div>

        {profiles.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No profiles found matching your search criteria.
            </p>
            <button
              onClick={() => navigate("/admin/search")}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Modify Search
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-32">
              {profiles.map((profile: any, index: any) => (
                <div
                  key={`${profile._id}-${index}`}
                  className="bg-white shadow-lg rounded-lg p-4 flex flex-col transition-transform duration-200 hover:shadow-xl hover:scale-105"
                >
                  {/* Profile Header with three-dots menu */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h2 className="text-lg font-bold">{profile.fullName}</h2>
                      <hr className="bg-slate-900" />
                      <span className="text-sm font-bold">
                        {calculateAge(profile.dateOfBirth)} yrs |{" "}
                        {profile.height} ft
                      </span>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="flex-1 text-sm">
                      <p className="text-gray-700">
                        <span className="text-gray-900 font-semibold">
                          Marital Status:
                        </span>{" "}
                        {profile.maritalStatus}
                      </p>
                      <p className="text-gray-700">
                        Marital ID: {profile.martialId}
                      </p>
                      <p>
                        Address: {profile.originalLocation || profile.district}
                      </p>
                      <p>
                        Caste: {profile.caste} - {profile.subcaste}
                      </p>
                      <p>Degree: {profile.education}</p>
                      <p>Income: ₹ {profile.annualIncome}</p>
                      <p>Profession: {profile.occupation}</p>
                      <p>
                        {profile.district}, {profile.state}, {profile.country}
                      </p>
                    </div>
                    {(() => {
                      const blurPhoto = shouldBlurProfileImage(
                        profile.imagePrivacy
                      );
                      const fallback =
                        "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png";
                      const imageSrc = profile.image
                        ? `${UPLOAD_URL}/users/profile_pic/${profile.image}`
                        : fallback;
                      return (
                        <div className="relative ml-4 w-28 h-36 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={imageSrc}
                            alt="Profile"
                            className={`w-full h-full object-cover ${
                              blurPhoto ? "blur-sm scale-110 brightness-90" : ""
                            }`}
                          />
                        </div>
                      );
                    })()}
                  </div>
                  {/* Buttons */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        try {
                          sessionStorage.setItem(
                            SCROLL_KEY_Y,
                            String(window.scrollY)
                          );
                          sessionStorage.setItem(
                            SCROLL_KEY_PATH,
                            window.location.pathname + window.location.search
                          );
                        } catch {}

                        navigate(`/${id}/${name}/${profile.martialId}`);
                      }}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      View More
                    </button>

                    <button
                      onClick={() => handleShareProfile(profile)}
                      className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-100 transition"
                    >
                      Share
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Section */}
            <div className="mt-8 mb-4">
              <div ref={loadMoreRef} className="h-4"></div>
              {loadingNext && (
                <div
                  style={{
                    position: "fixed",
                    left: 0,
                    width: "100%",
                    bottom: "20%",
                    zIndex: 50,
                    display: "flex",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div style={{ pointerEvents: "auto" }}>
                    <Loader />
                    <span className="ml-2 text-gray-600">
                      Loading next batch...
                    </span>
                  </div>
                </div>
              )}
              {hasMore && !loadingNext && (
                <div className="flex justify-center">
                  <button
                    onClick={handleLoadNext}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <span>Load Next 10 Profiles</span>
                    <span className="text-xs bg-blue-500 px-2 py-1 rounded">
                      Batch {Math.ceil(profiles.length / profilesPerPage) + 1}
                    </span>
                  </button>
                </div>
              )}
              {!hasMore && profiles.length > 0 && (
                <div className="text-center text-gray-500 py-4">
                  <p>All profiles loaded</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuickSearchResults;
